// Minimal FHIR types (to avoid depending on external fhir typings here)
export type FhirQuantity = {
  value?: number;
  unit?: string;
};

export type FhirCoding = {
  system?: string;
  code?: string;
  display?: string;
};

export type FhirCodeableConcept = {
  coding?: FhirCoding[];
};

export type FhirObservationReferenceRange = {
  resourceType?: string;
  low?: FhirQuantity;
  high?: FhirQuantity;
};

export type FhirResource = {
  resourceType?: string;
};

export type FhirObservation = {
  valueString?: string;
  valueQuantity?: FhirQuantity;
  code: { coding?: FhirCoding[] };
  contained?: Array<FhirResource | FhirObservationReferenceRange>;
  component?: Array<{
    valueQuantity?: FhirQuantity;
    code: { coding?: FhirCoding[] };
  }>;
};

export const SYSTEM_LOINC = "http://loinc.org";

/**
 * Interpretation codes in display order (left to right on reference range bar).
 * Order: Critically Low → Significantly Low → Low → Normal → High → Significantly High → Critically High
 */
export const INTERPRETATION_DISPLAY_ORDER = [
  "LL",
  "LU",
  "L",
  "N",
  "H",
  "HU",
  "HH"
] as const;

/**
 * Interpretation codes in evaluation order (inside-out checking logic).
 * Used when finding which range contains an observation value.
 * Checks normal first, then progressively outward to more severe ranges.
 */
export const INTERPRETATION_EVALUATION_ORDER = [
  "N",
  "L",
  "LU",
  "H",
  "HU",
  "LL",
  "HH"
] as const;

/**
 * Interpretation codes in severity order (least to most severe).
 * Used when determining the most severe interpretation among multiple values.
 */
export const INTERPRETATION_SEVERITY_ORDER = INTERPRETATION_EVALUATION_ORDER;

export type InterpretationCode = (typeof INTERPRETATION_DISPLAY_ORDER)[number];

export interface DecoratedObservationReferenceRange extends FhirObservationReferenceRange {
  interpretationCode?: FhirCodeableConcept;
  id?: string;
  code?: string;
  min?: number;
  max?: number;
  atLeastOneLimit?: boolean;
  onlyOneLimit?: boolean;
  widthPercentage?: number;
  cursorPositionPercentageFromLeft?: number;
}

export interface ObservationRangesData {
  observationValue?: number;
  ranges?: DecoratedObservationReferenceRange[];
  normalRange?: DecoratedObservationReferenceRange;
  normalRangeMin?: number;
  normalRangeMax?: number;
  totalWidth?: number;
}

export const sortObservationRangesByCode = (
  ranges: DecoratedObservationReferenceRange[],
  codeOrder: readonly string[] = INTERPRETATION_DISPLAY_ORDER
) => {
  return [...ranges].sort((a, b) => {
    const codeA = a.interpretationCode?.coding?.[0].code;
    const codeB = b.interpretationCode?.coding?.[0].code;
    const codeAIndex = codeOrder.findIndex((code) => code === codeA);
    const codeBIndex = codeOrder.findIndex((code) => code === codeB);

    return codeAIndex < codeBIndex ? -1 : 1;
  });
};

export const getCursorPositionPercentageFromLeft = (
  range: DecoratedObservationReferenceRange,
  value?: number
): number => {
  const min = range.low?.value;
  const max = range.high?.value;
  let percentage = 50;

  if (
    typeof value === "number" &&
    typeof min === "number" &&
    typeof max === "number"
  ) {
    percentage = ((value - min) / (max - min)) * 100;
  }

  return percentage;
};

export const getRangeWidthPercentage = (
  range?: DecoratedObservationReferenceRange,
  totalWidth?: number,
  onlyNormalRange: boolean = false
): number => {
  const rangeMin = range?.low?.value;
  const rangeMax = range?.high?.value;
  let percentage = 0;

  if (typeof rangeMin === "number" && typeof rangeMax === "number") {
    if (onlyNormalRange) {
      percentage = 50;
    } else {
      const rangeWidth = rangeMax - rangeMin;
      if (totalWidth) {
        percentage = (rangeWidth * 100) / totalWidth;
      }
    }
  } else if (typeof rangeMin === "number" || typeof rangeMax === "number") {
    percentage = 75;
  }

  if (percentage < 0) {
    percentage = 0;
  }
  if (percentage > 100) {
    percentage = 100;
  }

  return percentage;
};

export const getObservationTitle = (observation: FhirObservation) => {
  const codings: FhirCoding[] | undefined = observation.code.coding;
  let coding: FhirCoding | undefined = codings?.find(
    (item: FhirCoding) => item.system !== SYSTEM_LOINC
  );
  coding ??= codings?.[0];
  return coding?.display;
};

export interface ObservationData {
  observationValueString?: string;
  observationValue?: number;
  observationUnit?: string;
  observationTitle?: string;
  ranges?: DecoratedObservationReferenceRange[];
  isRangeDefined?: boolean;
}

export const getObservationData = (
  observation: FhirObservation
): ObservationData => {
  const observationValueString: string | undefined = observation.valueString;
  const observationValue: number | undefined = observation.valueQuantity?.value;
  const observationUnit: string | undefined = observation.valueQuantity?.unit;
  const observationTitle = getObservationTitle(observation);

  const ranges = observation.contained?.filter(
    (resource) => resource.resourceType === "ObservationRange"
  ) as DecoratedObservationReferenceRange[] | undefined;

  const isRangeDefined = ranges?.some(
    (range) =>
      typeof range.low?.value === "number" ||
      typeof range.high?.value === "number"
  );

  return {
    observationValueString,
    observationValue,
    observationUnit,
    observationTitle,
    ranges,
    isRangeDefined
  };
};

export const getObservationRangesData = (
  observation: FhirObservation
): ObservationRangesData => {
  const observationValue: number | undefined = observation.valueQuantity?.value;
  const ranges = observation.contained?.filter(
    (resource) => resource.resourceType === "ObservationRange"
  ) as DecoratedObservationReferenceRange[] | undefined;
  const normalRanges = ranges?.filter(
    (range) => range.interpretationCode?.coding?.[0].code === "N"
  );
  const normalRange = normalRanges?.[0];
  const normalRangeMin: number | undefined = normalRange?.low?.value;
  const normalRangeMax: number | undefined = normalRange?.high?.value;

  const minRangeValues = ranges?.map((range) => range.low?.value) ?? [];
  const maxRangeValues = ranges?.map((range) => range.high?.value) ?? [];
  const filteredMinRangeValues = minRangeValues.filter(
    (value) => typeof value === "number"
  );
  const filteredMaxRangeValues = maxRangeValues.filter(
    (value) => typeof value === "number"
  );
  const totalMin = filteredMinRangeValues.length
    ? Math.min(...filteredMinRangeValues)
    : undefined;
  const totalMax = filteredMaxRangeValues.length
    ? Math.max(...filteredMaxRangeValues)
    : undefined;
  const totalWidth =
    typeof totalMin === "number" && typeof totalMax === "number"
      ? totalMax - totalMin
      : undefined;

  const sortedObservationRanges = sortObservationRangesByCode(ranges ?? []);

  const onlyNormalRange = ranges?.length === 1 && Boolean(normalRange);

  const decoratedRanges: DecoratedObservationReferenceRange[] =
    sortedObservationRanges.map((range) => {
      const min = range.low?.value;
      const max = range.high?.value;

      const atLeastOneLimit =
        typeof min === "number" || typeof max === "number";
      const onlyOneLimit = typeof min !== "number" || typeof max !== "number";
      const widthPercentage = getRangeWidthPercentage(
        range,
        totalWidth,
        onlyNormalRange
      );
      const cursorPositionPercentageFromLeft =
        getCursorPositionPercentageFromLeft(range, observationValue);

      return {
        ...range,
        code: range.interpretationCode?.coding?.[0].code,
        min,
        max,
        atLeastOneLimit,
        onlyOneLimit,
        widthPercentage,
        cursorPositionPercentageFromLeft
      } as DecoratedObservationReferenceRange;
    });

  return {
    observationValue,
    ranges: decoratedRanges,
    normalRange,
    normalRangeMin,
    normalRangeMax,
    totalWidth
  };
};
